import cn from 'classnames'

import { Translation } from 'cng-common/features/translations/components/translation'
import { ITableColumn } from 'partner360/components/financialTable'
import { TRANSLATION_KEYS } from 'partner360/components/QBR/translations'
import { IQBRSobAchievement } from 'partner360/models/qbr/IQBRSobAchievement'

import { QbrQuarters } from '../../../../../../models/qbrQuarters'
import styles from '../SobAchievement.module.scss'

import { getSobAchievementCell } from './getSobAchievementCell'

interface IGetSobAchievementColumnsParameters {
  showUpToDateColumn: boolean
  selectedPeriodFQuarters: number[]
}

export const getSobAchievementColumns = ({
  showUpToDateColumn,
  selectedPeriodFQuarters,
}: IGetSobAchievementColumnsParameters): ITableColumn<IQBRSobAchievement>[] => {
  const previousYearColumns: ITableColumn<IQBRSobAchievement>[] = [
    {
      key: 'name',
      title: 'Name',
      renderFn: (item) => {
        return (
          <div className={styles.tableCol}>
            <p className={cn(styles.titleText, styles.name)}> {item.Name}</p>
            <div className={cn(styles.financialValue, styles.fullView)}>
              <p className={styles.paragraph}>
                <Translation text={TRANSLATION_KEYS.financialTable.yoyActual} />
              </p>
              <p className={styles.paragraph}>
                <Translation text={TRANSLATION_KEYS.financialTable.yoyPlan} />
              </p>
              <p className={styles.subParagraph}>
                <Translation text={TRANSLATION_KEYS.financialTable.yoySob} />
              </p>
            </div>
          </div>
        )
      },
    },
    {
      key: 'Q1',
      title: TRANSLATION_KEYS.financialTable.columns.Q1,
      isTranslatable: true,
      renderFn: (item) => {
        return getSobAchievementCell({
          actuals: item.PrevYearQ1Actuals,
          plans: item.PrevYearQ1Plans,
          yoyActuals: item.PrevYearQ1YoYActuals,
        })
      },
    },
    {
      key: 'Q2',
      title: TRANSLATION_KEYS.financialTable.columns.Q2,
      isTranslatable: true,
      renderFn: (item) => {
        return getSobAchievementCell({
          actuals: item.PrevYearQ2Actuals,
          plans: item.PrevYearQ2Plans,
          yoyActuals: item.PrevYearQ2YoYActuals,
        })
      },
    },
    {
      key: 'Q3',
      title: TRANSLATION_KEYS.financialTable.columns.Q3,
      isTranslatable: true,
      renderFn: (item) => {
        return getSobAchievementCell({
          actuals: item.PrevYearQ3Actuals,
          plans: item.PrevYearQ3Plans,
          yoyActuals: item.PrevYearQ3YoYActuals,
        })
      },
    },
    {
      key: 'Q4',
      title: TRANSLATION_KEYS.financialTable.columns.Q4,
      isTranslatable: true,
      renderFn: (item) => {
        return getSobAchievementCell({
          actuals: item.PrevYearQ4Actuals,
          plans: item.PrevYearQ4Plans,
          yoyActuals: item.PrevYearQ4YoYActuals,
        })
      },
    },
  ]

  if (showUpToDateColumn) {
    previousYearColumns.push({
      key: 'Total UTD',
      title: TRANSLATION_KEYS.financialTable.columns.totalUpToDate,
      isTranslatable: true,
      renderFn: (item) => {
        return getSobAchievementCell({
          actuals: item.PrevYearTFYActuals,
          plans: item.PrevYearTFYPlans,
          yoyActuals: item.PrevYearTFYYoYActuals,
          isTotal: true,
        })
      },
    })
  }

  previousYearColumns.push({
    key: 'Total',
    title: TRANSLATION_KEYS.financialTable.columns.total,
    isTranslatable: true,
    renderFn: (item) => {
      return getSobAchievementCell({
        actuals: item.PrevYearFYActuals,
        plans: item.PrevYearFYPlans,
        yoyActuals: item.PrevYearFYYoYActuals,
        isTotal: true,
        isAlternativeTotal: showUpToDateColumn,
      })
    },
  })

  const currentYearColumns: ITableColumn<IQBRSobAchievement>[] = []

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q1)) {
    currentYearColumns.push({
      key: 'Q1',
      title: TRANSLATION_KEYS.financialTable.columns.Q1,
      isTranslatable: true,
      renderFn: (item) => {
        return getSobAchievementCell({
          actuals: item.CurrentYearQ1Actuals,
          plans: item.CurrentYearQ1Plans,
          yoyActuals: item.CurrentYearQ1YoYActuals,
        })
      },
    })
  }

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q2)) {
    currentYearColumns.push({
      key: 'Q2',
      title: TRANSLATION_KEYS.financialTable.columns.Q2,
      isTranslatable: true,
      renderFn: (item) => {
        return getSobAchievementCell({
          actuals: item.CurrentYearQ2Actuals,
          plans: item.CurrentYearQ2Plans,
          yoyActuals: item.CurrentYearQ2YoYActuals,
        })
      },
    })
  }

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q3)) {
    currentYearColumns.push({
      key: 'Q3',
      title: TRANSLATION_KEYS.financialTable.columns.Q3,
      isTranslatable: true,
      renderFn: (item) => {
        return getSobAchievementCell({
          actuals: item.CurrentYearQ3Actuals,
          plans: item.CurrentYearQ3Plans,
          yoyActuals: item.CurrentYearQ3YoYActuals,
        })
      },
    })
  }

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q4)) {
    currentYearColumns.push({
      key: 'Q4',
      title: TRANSLATION_KEYS.financialTable.columns.Q4,
      isTranslatable: true,
      renderFn: (item) => {
        return getSobAchievementCell({
          actuals: item.CurrentYearQ4Actuals,
          plans: item.CurrentYearQ4Plans,
          yoyActuals: item.CurrentYearQ4YoYActuals,
        })
      },
    })
  }

  currentYearColumns.push({
    key: 'Total',
    title: showUpToDateColumn
      ? TRANSLATION_KEYS.financialTable.columns.totalUpToDate
      : TRANSLATION_KEYS.financialTable.columns.total,
    isTranslatable: true,
    renderFn: (item) => {
      return getSobAchievementCell({
        actuals: item.CurrentYearFYActuals,
        plans: item.CurrentYearFYPlans,
        yoyActuals: item.CurrentYearFYYoYActuals,
        isTotal: true,
      })
    },
  })

  return [...previousYearColumns, ...currentYearColumns]
}
